import type { components } from '@trackercop/contracts';
import { Badge } from '@/components/ui/badge';

type CapacityResource = components['schemas']['CapacityResource'];

interface CapacityBarProps {
  resource: CapacityResource;
}

function formatValue(value: number, unit: CapacityResource['unit']): string {
  switch (unit) {
    case 'bytes':
      return formatBytes(value);
    case 'days':
      return `${value.toLocaleString()}d`;
    case 'requests':
    case 'count':
    default:
      return value.toLocaleString();
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)}${units[i]}`;
}

function barTone(percent: number | null | undefined): {
  bar: string;
  variant: 'success' | 'warning' | 'destructive';
} {
  const p = percent ?? 0;
  if (p >= 90) return { bar: 'bg-red-500', variant: 'destructive' };
  if (p >= 70) return { bar: 'bg-amber-500', variant: 'warning' };
  return { bar: 'bg-emerald-500', variant: 'success' };
}

export function CapacityBar({ resource }: CapacityBarProps) {
  const percent = resource.percent ?? null;
  const tone = barTone(percent);
  const widthPct = percent === null ? 0 : Math.min(100, Math.max(0, percent));
  return (
    <div
      className="flex flex-col gap-1.5 rounded-md border bg-background p-3"
      data-testid={`capacity-bar-${resource.name}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{resource.name}</span>
        {resource.tier && (
          <Badge variant="outline" className="text-[10px]">
            {resource.tier}
          </Badge>
        )}
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full ${tone.bar}`}
          style={{ width: `${widthPct}%` }}
          data-testid={`capacity-bar-fill-${resource.name}`}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-mono">
          {formatValue(resource.used, resource.unit)}
          {resource.limit != null
            ? ` / ${formatValue(resource.limit, resource.unit)}`
            : ''}
        </span>
        <Badge variant={tone.variant}>
          {percent === null ? '—' : `${percent.toFixed(1)}%`}
        </Badge>
      </div>
    </div>
  );
}
